import React, { useState, useEffect } from "react";
import { FaTrashCan, FaPen, FaShare } from "react-icons/fa6";
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { formatDistanceToNow } from 'date-fns';
import AddServicePopup from '../AddServicePopup';
import PurchasePopup from "../../../../Shared/PurchasePopup/PurchasePopup";
import { toast } from 'react-hot-toast';
import { load } from "@cashfreepayments/cashfree-js";
import { 
  createPaymentSession,
  verifyPayment
} from "../../../../../Service/user";
import './ServiceCards.scss';
import { useNavigate } from 'react-router-dom';
import SharePopup from "../../../../PopUp/SocialSharePopup/SharePopup";

const MeetingCard = ({
  meeting,
  isAdmin,
  loggedInUserId,
  onDelete,
  onUpdate,
  onBook,
  communityId
}) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showTimeSlots, setShowTimeSlots] = useState(false);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [showRegisteredUsers, setShowRegisteredUsers] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [localMeeting, setLocalMeeting] = useState(meeting);
  const [isBooking, setIsBooking] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [sharePopupOpen, setSharePopupOpen] = useState(false);
  const [socialUrl, setSocialUrl] = useState('');
  const navigate = useNavigate();
  const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser"));
  useEffect(() => {
    setLocalMeeting(meeting);
  }, [meeting]);

  // Filter out booked slots and dates with no available slots
  const getAvailableDates = () => {
    if (!localMeeting.availability) return [];
    
    return localMeeting.availability.filter(day => {
      // Check if the day is in the future with normalized dates
      const dayDate = new Date(day.day);
      dayDate.setHours(12, 0, 0, 0);
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Get available (not booked) slots for this day
      const availableSlots = day.slots.filter(slot => !slot.isBooked);
      
      // Return true only if the day is in the future and has available slots
      return dayDate >= today && availableSlots.length > 0;
    });
  };

  const handleDateSelect = (date) => {
    // Set the time to noon to avoid timezone issues
    const normalizedDate = new Date(date.setHours(12, 0, 0, 0));
    setSelectedDate(normalizedDate);
    
    // Find available slots for selected date
    const selectedDay = localMeeting.availability.find(
      day => {
        const availableDay = new Date(day.day);
        availableDay.setHours(12, 0, 0, 0);
        return availableDay.toDateString() === normalizedDate.toDateString();
      }
    );

    if (selectedDay) {
      // Filter out booked slots and sort by start time
      const availableSlots = selectedDay.slots
        .filter(slot => !slot.isBooked)
        .sort((a, b) => {
          const timeA = new Date(`1970/01/01 ${a.startTime}`);
          const timeB = new Date(`1970/01/01 ${b.startTime}`);
          return timeA - timeB;
        });
        
      setAvailableTimeSlots(availableSlots);
      setShowTimeSlots(true);
    }
  };

  const handleTimeSlotSelect = (slot) => {
    setSelectedTimeSlot(slot);
  };

  const handleConfirmBooking = async () => {
    setIsBooking(true);
    try {
      // For paid meetings, show payment modal first
      if (localMeeting.amount > 0) {
        setShowPaymentModal(true);
        return;
      }

      await processBooking();
    } catch (error) {
      console.error("Error booking slot:", error);
      toast.error("Failed to book meeting");
    } finally {
      setIsBooking(false);
    }
  };

  const processBooking = async (paymentDetails = null) => {
    try {
      // Find the selected day in availability
      const selectedDayIndex = localMeeting.availability.findIndex(
        slot => {
          const availableDay = new Date(slot.day);
          availableDay.setHours(12, 0, 0, 0);
          return availableDay.toDateString() === selectedDate.toDateString();
        }
      );

      // Prepare booking data with normalized date
      const bookingData = {
        day: new Date(selectedDate.setHours(12, 0, 0, 0)).toISOString(),
        startTime: selectedTimeSlot.startTime,
        endTime: selectedTimeSlot.endTime
      };

      // Wait for the booking response
      const response = await onBook(bookingData);
  
       // Create new booking object
      const newBooking = {
        userId: { 
          _id: loggedInUserId,
        },
        slot: {
          day: bookingData.day,
          startTime: bookingData.startTime,
          endTime: bookingData.endTime,
          meeting_link: response.slot.meeting_link || "pending" // Include meeting link from response
        },
        createdAt: new Date().toISOString(),
      };

      // Update local state immediately
      setLocalMeeting(prevMeeting => {
        const updatedAvailability = [...prevMeeting.availability];
        updatedAvailability[selectedDayIndex] = {
          ...updatedAvailability[selectedDayIndex],
          slots: updatedAvailability[selectedDayIndex].slots.map(slot =>
            slot.startTime === selectedTimeSlot.startTime
              ? { ...slot, isBooked: true }
              : slot
          )
        };

        return {
          ...prevMeeting,
          bookings: [...(prevMeeting.bookings || []), newBooking],
          availability: updatedAvailability
        };
      });

      // Reset UI states
      setShowTimeSlots(false);
      setSelectedDate(null);
      setShowCalendar(false);
      setSelectedTimeSlot(null);
      setShowPaymentModal(false);

      toast.success("Meeting booked successfully!");
    } catch (error) {
      console.error("Error processing booking:", error);
      toast.error("Failed to book meeting");
      throw error;
    }
  };

  const initializeCashfree = async () => {
    try {
      return await load({ mode: "production" });
    } catch (error) {
      console.error("Failed to initialize Cashfree:", error);
      throw error;
    }
  };

  const handlePaymentSuccess = async (userDetails) => {
    try {
      const cashfree = await initializeCashfree();

      const paymentResponse = await createPaymentSession({
        name: userDetails.name,
        email: userDetails.email,
        mobile: userDetails.mobile,
        amount: localMeeting.amount,
        entityId: localMeeting._id,
        entityType: "meeting",
      });

      const { order_id, payment_session_id } = paymentResponse.data;

      await cashfree.checkout({
        paymentSessionId: payment_session_id,
        redirectTarget: "_modal",
      });

      const verificationResponse = await verifyPayment({
        orderId: order_id,
        entityId: localMeeting._id,
        entityType: "meeting",
      });

      if (verificationResponse.data.status === "SUCCESS") {
        await processBooking();
        toast.success("Payment successful and meeting booked!");
      } else {
        toast.error("Payment verification failed");
      }
    } catch (error) {
      console.error("Payment error:", error);
      toast.error("Payment process failed");
    } finally {
      setShowPaymentModal(false);
    }
  };

  const handleBackToCalendar = () => {
    setShowTimeSlots(false);
    setSelectedDate(null);
  };

  const handleEdit = () => {
    if (onUpdate) {
      onUpdate(localMeeting);
    }
  };

  const handleEditComplete = (updatedMeeting) => {
    setShowEditPopup(false);
    if (onUpdate) {
      onUpdate(updatedMeeting);
    }
  };
  // Add this function to check if user has booked this meeting
  const getUserBooking = () => {
    return localMeeting.bookings?.find(booking => 
      booking.userId._id === loggedInUserId || booking.userId === loggedInUserId
    );
  };

  const handleOpenSocialShare = (e) => {
   
    // Generate the meeting detail URL
    const baseUrl = window.location.origin;
    const meetingUrl = `${baseUrl}/community/${communityId}/meeting/${meeting._id}`;
    setSocialUrl(meetingUrl);
    setSharePopupOpen(true);
  };

  const handleCardClick = () => {
    navigate(`/community/${communityId}/meeting/${meeting._id}`);
  };

  return (
    <>
      <div className="service-card meeting-card" onClick={handleCardClick}>
        <div className="service-content">
          <h3>{localMeeting.title}</h3>
          <div 
            className="description"
            dangerouslySetInnerHTML={{ __html: localMeeting.description }}
          />
          <div className="service-details">
            <span>Duration: {localMeeting.duration} minutes</span>
            <span className={`badge ${localMeeting.amount === 0 ? "free" : "paid"}`}>
              {localMeeting.amount === 0 ? "Free" : `₹${localMeeting.amount}`}
            </span>
          </div>
          <div className="topics">
            {localMeeting.topics.map((topic, index) => (
              <span key={index} className="topic-tag">
                {topic}
              </span>
            ))}
          </div>
        </div>

        {!isAdmin && (
          <div className="action-section">
            {getUserBooking() ? (
              <div className="booked-status">
                <button className="book-button booked">
                  Booked!
                </button>
                <div className="booking-details">
                  <span>{new Date(getUserBooking().slot.day).toLocaleDateString('en-US', { 
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}</span>
                  <span>{getUserBooking().slot.startTime} - {getUserBooking().slot.endTime}</span>
                  {getUserBooking().slot.meeting_link && (
                    <a 
                      href={getUserBooking().slot.meeting_link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="join-meeting-button"
                    >
                      Join Meeting
                    </a>
                  )}
                </div>
              </div>
            ) : (
              <button
                className="book-button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowCalendar(true);
                }}
              >
                Book Meeting
              </button>
            )}
          </div>
        )}

        {isAdmin && (
          <div className="admin-actions">
            <button 
              className="view-bookings-button"
              onClick={(e) => {
                e.stopPropagation();
                setShowRegisteredUsers(true);
              }}
            >
              View Bookings
              {localMeeting.bookings?.length > 0 && (
                <span className="bookings-badge">
                  {localMeeting.bookings.length}
                </span>
              )}
            </button>
            <button 
              className="edit-button" 
              onClick={(e) => {
                e.stopPropagation();
                handleEdit();
              }}
            >
              <FaPen size={20} />
            </button>
            <button className="delete-button" onClick={(e) => {
              e.stopPropagation();
              if (window.confirm('Are you sure you want to delete this meeting?')) {
                onDelete();
              }
            }}>
              <FaTrashCan size={20} />
            </button>
          </div>
        )}

        <div className="card-actions">
          <button className="share-button" onClick={(e) => {
            e.stopPropagation();
            handleOpenSocialShare();
          }}>
            <FaShare size={20} />
          </button>
        </div>
      </div>

      {/* Calendar Modal */}
      {showCalendar && (
        <div className="modal-overlay">
          <div className="calendar-modal">
            <div className="modal-header">
              <h3>{showTimeSlots ? 'Select Time Slot' : 'Select Meeting Date'}</h3>
              <button 
                className="close-button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowCalendar(false);
                  setShowTimeSlots(false);
                  setSelectedDate(null);
                }}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              {!showTimeSlots ? (
                <div className="calendar-container">
                  <Calendar
                    onChange={handleDateSelect}
                    value={selectedDate}
                    minDate={new Date()}
                    tileDisabled={({ date }) => {
                      const availableDates = getAvailableDates();
                      return !availableDates.some(
                        availableDay => new Date(availableDay.day).toDateString() === date.toDateString()
                      );
                    }}
                    tileClassName={({ date }) => {
                      const availableDates = getAvailableDates();
                      return availableDates.some(
                        availableDay => new Date(availableDay.day).toDateString() === date.toDateString()
                      ) ? 'available-date' : '';
                    }}
                  />
                </div>
              ) : (
                <div className="time-slots-container">
                  <p className="selected-date">{selectedDate.toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}</p>
                  <div className="meeting-time-slots-grid">
                    {availableTimeSlots.map((slot, index) => (
                      <button
                        key={index}
                        className={`meeting-time-slot-button ${selectedTimeSlot === slot ? 'meeting-time-slot-selected' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTimeSlotSelect(slot);
                        }}
                      >
                        {slot.startTime} - {slot.endTime}
                      </button>
                    ))}
                  </div>
                  <div className="meeting-action-buttons">
                    <button 
                      className="meeting-back-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleBackToCalendar();
                      }}
                    >
                      Back to Calendar
                    </button>
                    {selectedTimeSlot && (
                      <button 
                        className="meeting-confirm-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleConfirmBooking();
                        }}
                        disabled={isBooking}
                      >
                        {isBooking ? 'Booking...' : 'Confirm Booking'}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showRegisteredUsers && (
        <div className="admin-modal-overlay">
          <div className="bookings-modal">
            <div className="modal-header">
              <h3>Meeting Bookings</h3>
              <button 
                className="close-button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowRegisteredUsers(false);
                }}
              >
                ×
              </button>
            </div>
            <div className="bookings-list">
              {localMeeting.bookings?.length > 0 ? (
                localMeeting.bookings.map((booking, index) => (
                  <div key={index} className="booking-item">
                    <div className="booking-header">
                      <div className="user-info">
                        <img 
                          src={booking.userId.profilePicture} 
                          alt="Profile" 
                          className="user-avatar"
                        />
                        <span className="user-name">
                          {`${booking.userId.firstName} ${booking.userId.lastName}`}
                        </span>
                      </div>
                      <span className="booking-time">
                        {new Date(booking.slot.day).toLocaleDateString('en-US', { 
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })} at {booking.slot.startTime}
                      </span>
                    </div>
                    <div className="booking-details">
                      <span className="booking-timestamp">
                        Booked {formatDistanceToNow(new Date(booking.createdAt))} ago
                      </span>
                    </div>
                    <div className="booking-actions">
                      <a 
                        href={booking.slot.meeting_link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="join-meeting-button"
                      >
                        Join Meeting
                      </a>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-bookings">
                  No bookings available yet
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showEditPopup && (
        <AddServicePopup
          communityId={communityId}
          onClose={() => setShowEditPopup(false)}
          onServiceAdded={handleEditComplete}
          editingService={{
            ...localMeeting,
            serviceType: 'meeting'
          }}
        />
      )}

      {showPaymentModal && (
        <PurchasePopup
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          itemDetails={{
            type: "meeting",
            name: localMeeting.title,
            description: localMeeting.description,
            duration: localMeeting.duration,
            selectedSlot: {
              date: selectedDate,
              time: `${selectedTimeSlot.startTime} - ${selectedTimeSlot.endTime}`
            },
            amount: localMeeting.amount,
            is_free: localMeeting.amount === 0,
          }}
          loggedInUser={loggedInUser}
          onProceed={handlePaymentSuccess}
        />
      )}

      {sharePopupOpen && (
        <SharePopup
          postId={meeting._id}
          isOpen={sharePopupOpen}
          setIsOpen={setSharePopupOpen}
          url={socialUrl}
        />
      )}
    </>
  );
};

export default MeetingCard; 